import { AfterViewInit, Component, effect, EffectRef, ElementRef, inject, Renderer2, Signal, untracked, ViewChild } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { DvmService } from '@services/dvm.service';
import anime from 'animejs';
import { version } from './../../../../../../package.json';

@Component({
    selector: 'app-legend',
    imports: [
        ButtonComponent
    ],
    standalone: true,
    templateUrl: './legend.component.html',
    styleUrl: './legend.component.css'
})
export class LegendComponent implements AfterViewInit {

  @ViewChild('legendBtn', { read: ElementRef })
  private readonly legendBtn!: ElementRef<HTMLElement>;

  @ViewChild('legendContent', { static: true, read: ElementRef})
  public legendContent!: ElementRef<HTMLDivElement>;

  // SERVICES
  private readonly dvmService:   DvmService               = inject(DvmService);
  private readonly renderer:     Renderer2                = inject(Renderer2);

  // GLOBAL STATE
  public readonly  view:         Signal<AppViews | null>  = this.dvmService.view;

  // BUTTON STYLE
  public readonly  type:         ButtonType               = ButtonType.Pill;

  // COMPONENT STATE
  private          viewReady:        boolean              = false;
  private          legendVisible:    boolean              = false;
  private          containerVisible: boolean              = false;
  private          desiredLegendVisible: boolean          = this.view() !== AppViews.Pano;
  protected version: string = version;

  // EFFECTS
  private readonly onViewChange:  EffectRef                = effect((): void => {

    const view: AppViews | null = this.view(),
          show: boolean = view !== AppViews.Pano;

    if(view === null) return;
    
    this.desiredLegendVisible = show;

    if(this.viewReady) {
      window.setTimeout((): void => this.animateLegend(show), this.legendVisible ? 300 : 0);
    }

    untracked((): void => this.closeContainer());

  } );

  public ngAfterViewInit(): void {
    this.viewReady = true;
    this.setElementHidden(this.legendBtn.nativeElement, !this.desiredLegendVisible);
    this.setElementHidden(this.legendContent.nativeElement, true);
    this.animateLegend(this.desiredLegendVisible);
  }

  private animateLegend(show: boolean): void {
    if(show === this.legendVisible) return;

    const legendButton = this.legendBtn.nativeElement;

    anime.remove(legendButton);
    this.legendVisible = show;

    if(show) this.setElementHidden(legendButton, false);
    
    anime({
      complete: (): void => {
        if(!show) this.setElementHidden(legendButton, true);
      },
      targets:  legendButton,
      opacity:  show ? 1 : 0,
      duration: 300,
      easing:   'easeInOutQuad',
    })

  }

  protected toggleContainer(): void {
    this.setContainerVisible(!this.containerVisible);
  }

  private closeContainer(): void {
    this.setContainerVisible(false);
  }

  private setContainerVisible(show: boolean): void {
    if(show === this.containerVisible) return;

    const legendContent = this.legendContent.nativeElement,
          legendButton = this.getNativeButton(this.legendBtn.nativeElement);

    anime.remove(legendContent);
    this.containerVisible = show;

    if(show) {
      this.setElementHidden(legendContent, false);
      this.renderer.addClass(legendButton, 'active');
    } else {
      this.renderer.removeClass(legendButton, 'active');
    }

    anime.timeline({
      complete: (): void => { 
        if(!show) this.setElementHidden(legendContent, true);
      }, 
      duration: show ? 300 : 200,
      easing: show ? 'easeInOutQuad' : 'easeInOutCubic',
    })
    .add({
      targets: legendContent,
      opacity: show ? 1 : 0,
    })
  }

  private setElementHidden(element: HTMLElement, hidden: boolean): void {
    if(hidden) {
      this.renderer.addClass(element, 'hidden');
      this.renderer.addClass(element, 'pointer-events-none');
      return;
    }

    this.renderer.removeClass(element, 'hidden');
    this.renderer.removeClass(element, 'pointer-events-none');
  }

  private getNativeButton(host: HTMLElement): HTMLButtonElement {
    return host.querySelector('button')!;
  }

}
